/**
 * EmployeeListPage — Issue #176.
 *
 * Read-only employee directory. PII columns show last4 only; reveal is out
 * of scope for this Issue (separate Issue + audit required).
 *
 * 红线：
 *   - 仅使用 useHrEmployees hook，不手写 fetch。
 *   - 仅展示 last4；身份证 / 手机号明文展示需另立 reveal Issue。
 *   - 列表不展示 tenantId / orgId / cipher / hash。
 */
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_EMPLOYMENT_STATUS_LABELS,
  HR_EMPLOYMENT_STATUS_LIST,
  useHrEmployees,
  type HrEmploymentStatus,
} from '@/hooks/useHr';

function fmt(value?: string | null): string {
  return value && value.length > 0 ? value : '—';
}

function mask4(value: string | null | undefined, label: string): string {
  return value && value.length >= 1 ? `${label}***${value}` : '—';
}

export default function EmployeeListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.READ);
  const canSeeImports = hasPermission(permissions, HR_PERMISSIONS.IMPORT_READ);

  const { items, total, loading, error, fetchEmployees } = useHrEmployees();
  const [statusFilter, setStatusFilter] = useState<HrEmploymentStatus | ''>('');
  const [nameFilter, setNameFilter] = useState('');
  const [department, setDepartment] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchEmployees({
      employmentStatus: statusFilter || undefined,
      nameContains: nameFilter || undefined,
      department: department || undefined,
      page,
      limit,
    });
  }, [canRead, statusFilter, nameFilter, department, page, fetchEmployees]);

  if (!canRead) return <AccessDenied resource="员工档案" />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">员工花名册</h1>
          <p className="text-sm text-slate-500">
            身份证 / 手机号默认仅展示后四位；明文查看需另立 reveal Issue 并经过专项审计。
          </p>
        </div>
        {canSeeImports ? (
          <Link
            href="/hr/imports"
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
          >
            导入历史与新建导入 →
          </Link>
        ) : null}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          姓名包含
          <input
            type="text"
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={nameFilter}
            onChange={(e) => {
              setNameFilter(e.target.value);
              setPage(1);
            }}
            placeholder="FIXT-甲一-001"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          部门
          <input
            type="text"
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={department}
            onChange={(e) => {
              setDepartment(e.target.value);
              setPage(1);
            }}
            placeholder="研发中心"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          在职状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                e.target.value === ''
                  ? ''
                  : (e.target.value as HrEmploymentStatus),
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {HR_EMPLOYMENT_STATUS_LIST.map((s) => (
              <option key={s} value={s}>
                {HR_EMPLOYMENT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      ) : null}

      <Card className="overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">加载中…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            暂无员工记录。请先通过“导入历史”创建一个员工花名册导入。
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">工号</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">姓名</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">部门</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">岗位</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">入职日期</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">在职状态</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">手机后四位</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">身份证后四位</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">最近导入批次</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-3 font-mono text-xs">{row.employeeNo}</td>
                  <td className="px-4 py-3 font-medium">{row.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmt(row.department)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmt(row.position)}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(row.hireDate)}</td>
                  <td className="px-4 py-3 text-xs">
                    {HR_EMPLOYMENT_STATUS_LABELS[row.employmentStatus]}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{mask4(row.phoneLast4, '手机')}</td>
                  <td className="px-4 py-3 font-mono text-xs">{mask4(row.idCardLast4, '身份证')}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {row.importBatchId ? row.importBatchId.slice(0, 8) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 ? (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            上一页
          </button>
          <span className="text-muted-foreground">
            {page} / {totalPages}（共 {total} 条）
          </span>
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </button>
        </div>
      ) : null}
    </div>
  );
}
